// @flow

import * as React from 'react';
import { Environment, Preference } from '@ta/lithium.context';
import { getLocalezoneMessages } from '@ta/platform.ssr-cache';
import { parse, getFormattedMessage } from 'localezone';
import IntlFormatter from './intl-formatter';

export type MessageBundle = { +[key: string]: string };

type MessagesContext = {|
  +getMessage: string => string | null,
  +loadMessages: MessageBundle => void,
|};

const Messages = React.createContext<MessagesContext>({
  getMessage: () => null,
  loadMessages: () => undefined,
});
if (DEBUG) {
  Messages.displayName = 'Messages';
}

const createLocalezoneFormatter = intlFormatter => ({
  number: (val, loc, args) => {
    if (args[0] === 'percentage') {
      const type = args[1] === 'one' || args[1] === 'integer' ? args[1] : 'default';
      return intlFormatter.formatPercentage(type, val);
    } else if (args[0] === 'currency') {
      if (args[1] === 'integer') {
        return intlFormatter.formatCurrencyInteger(val);
      } else if (args[1] === 'iso_default') {
        return intlFormatter.formatCurrencyISO(val);
      } else if (args[1] === 'iso_integer') {
        return intlFormatter.formatCurrencyISOInteger(val);
      }
      return intlFormatter.formatCurrency(val);
    }
    return intlFormatter.formatNumber('default', val);
  },
  date: (val, loc, args) => intlFormatter.formatDate(args || 'default', val),
});

const localezoneCache: { [string]: any } = getLocalezoneMessages();

const localizeReact = (
  key: string,
  messageArgs?: { +[string]: React.Node },
  locale: string,
  getMessage: string => string | null,
  localezoneFormatter,
): React.Node => {
  const message = getMessage(key);
  if (message === null) {
    // Return this message in an array so that localizeString can handle exclusively arrays.
    return [`${key}${messageArgs ? `: ${Object.entries(messageArgs).toString()}` : ''}`];
  }
  if (!messageArgs) {
    return [message];
  }

  const cacheKey = `${locale}${key}`;
  try {
    if (!localezoneCache.has(cacheKey)) {
      localezoneCache.set(cacheKey, parse(message));
    }

    return getFormattedMessage(localezoneCache.get(cacheKey), messageArgs || {}, {
      locale,
      formatters: localezoneFormatter,
    });
  } catch (e) {
    console.error(`Exception while localizing ${key}:`, e);
    return key;
  }
};

const localizeString = (
  key: string,
  messageArgs?: { +[string]: string | number },
  locale: string,
  getMessage: string => string | null,
  localezoneFormatter,
): string => {
  // We are asserting that we get exclusively string tokens back from react if strings or numbers are all we give it.
  const reactNode = ((localizeReact(
    key,
    messageArgs,
    locale,
    getMessage,
    localezoneFormatter,
  ): any): Array<string>);
  return reactNode.reduce((acc, val) => acc + (val || ''));
};

/**
 * A hook that provides access to an IntlFormatter for the current locale and currency.
 */
const useIntlFormatter = (): IntlFormatter => {
  const {
    locale: { code: ietfLocale },
  } = React.useContext(Environment);
  const { currency } = React.useContext(Preference);
  return React.useMemo(() => new IntlFormatter(ietfLocale, currency), [ietfLocale, currency]);
};

/** Type of the function that the useLocalize hook returns */
export type LocalizeType = {
  (key: string, messageArgs?: { +[string]: React.Node }): React.Node,
  string: (key: string, messageArgs?: { +[string]: React.Node }) => string,
};

/**
 * A hook that provides access to two `localize` functions: `localize` allows message arguments to be React.Nodes and
 * returns a React.Node while `localize.string` only allows numbers or strings and returns a string.
 * Numbers passed in octothorpes to plurals will be formatted, so don't pass in formatted values -- it will cause issues.
 *
 * Prefer `localize` unless you have an explicit need for a string return value.
 */
const useLocalize = (): { localize: LocalizeType } => {
  const {
    locale: { code: ietfLocale },
  } = React.useContext(Environment);
  const { currency } = React.useContext(Preference);
  const messages = React.useContext(Messages);
  const localezoneFormatter = React.useMemo(
    () => createLocalezoneFormatter(new IntlFormatter(ietfLocale, currency)),
    [ietfLocale, currency],
  );

  const localize = React.useMemo(() => {
    const localizeInstance = (key: string, messageArgs?: { +[string]: React.Node }) =>
      localizeReact(key, messageArgs, ietfLocale, messages.getMessage, localezoneFormatter);
    // localize to a string instead of the default behavior of localizing to a React.Node.
    localizeInstance.string = (key: string, messageArgs?: { +[string]: string | number }) =>
      localizeString(key, messageArgs, ietfLocale, messages.getMessage, localezoneFormatter);
    return localizeInstance;
  }, [ietfLocale, localezoneFormatter, messages.getMessage]);

  return {
    localize,
  };
};

type LocalizeProps = {| +id: string, +args?: { +[string]: React.Node } |};
type UnsafeLocalizeProps = {|
  +id: string,
  +args?: { +[string]: number | string },
  +langAttribute?: ?string,
|};
const localizePropsEqual = (prevProps, nextProps) => {
  if (prevProps.id !== nextProps.id) {
    return false;
  }
  if (prevProps.args && nextProps.args) {
    const prevKeys = Object.keys(prevProps.args);
    if (prevKeys.length !== Object.keys(nextProps.args).length) {
      return false;
    }

    for (let i = 0; i < prevKeys.length; i++) {
      const key = prevKeys[i];
      if (prevProps.args[key] !== nextProps.args[key]) {
        return false;
      }
    }
  }
  return true;
};
const unsafeLocalizePropsEqual = (prevProps, nextProps) =>
  localizePropsEqual(prevProps, nextProps) && prevProps.langAttribute === nextProps.langAttribute;

/**
 * A simple component that delegates to {@link #useLocalize}.
 *
 * (We can't have `args` be a rest property as many translation string arguments have names that are not valid
 * identifiers, like '0').
 */
const Localize = ({ id, args }: LocalizeProps) => {
  const { localize } = useLocalize();
  // This lint also looks for uses of this component, so this is a safe escape.
  // eslint-disable-next-line ta-code-conventions/localize-non-string-literals
  return localize(id, args);
};
const LocalizeMemo = React.memo<LocalizeProps>(Localize, localizePropsEqual);

/**
 * A simple component that delegates to {@link #useLocalize} and treats localized string as HTML.
 *
 * (We can't have `args` be a rest property as many translation string arguments have names that are not valid
 * identifiers, like '0').
 */
const UnsafeLocalize = ({ id, args, langAttribute = null }: UnsafeLocalizeProps) => {
  const { localize } = useLocalize();
  // This lint also looks for uses of this component, so this is a safe escape.
  return (
    <span
      lang={langAttribute}
      // eslint-disable-next-line ta-code-conventions/localize-non-string-literals,react/no-danger
      dangerouslySetInnerHTML={{ __html: localize.string(id, args) }}
    />
  );
};

const UnsafeLocalizeMemo = React.memo<UnsafeLocalizeProps>(
  UnsafeLocalize,
  unsafeLocalizePropsEqual,
);

export {
  useLocalize,
  useIntlFormatter,
  IntlFormatter,
  UnsafeLocalizeMemo as UnsafeLocalize,
  Messages,
};
export default LocalizeMemo;
